import React from 'react'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import {
  COLUMN_TYPES,
  COLUMN_STANDARD_SIZES,
  DATA_TYPES,
  SEARCH_TYPES,
  LOOKUP_TYPES,
  constants,
  coreConstants,
  Icon,
  reducers,
  utils,
  Popup,
} from '@adc/ui-morpheus'
import { USER_REGISTERED_LISTINGS } from '../../constants/tableTypes'

const {
  actionCellActionTypes: { ACTION_CELL_ACTION_TYPES },
  optionLists: {
    globalPhysicalStructures,
    trusteeSalesChannel,
    productTypes,
    assetTypes,
    schedulingStatus,
    schedulingFailureReason,
  },
} = constants
const {
  date: { API_LOCAL_DATE_FORMAT, UI_DATE_ALTERNATE_FORMAT },
} = utils
const {
  states: statesList,
  yesNo: yesNoList,
  listingStatus: listingStatusList,
} = coreConstants.optionLists

const defaultColProps = { isResizable: true, isVisible: true }
const defaultColPropsWithWidth200 = { width: 200, ...defaultColProps }
const defaultColPropsWithWidth100 = { width: 100, ...defaultColProps }
export const tableConfig = {
  config: {
    entityKey: USER_REGISTERED_LISTINGS,
    primaryKeyField: 'listing_id',
  },
  fieldDefinitions: [
    {
      apiRef: 'listing_id',
      name: 'Listing ID',
      formatOptions: { custom: 'listingIdFormatter' },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'venue.venue_id',
      name: 'Venue ID',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.property_id',
      name: 'Property ID',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'event.event_code',
      name: 'Event Code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'event.event_type',
      name: 'Auction Type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
      options: [
        { value: 'LIVE', text: 'Live' },
        { value: 'ONLINE', text: 'Online' },
      ],
    },
    {
      apiRef: 'trustee_sale_number',
      name: 'Trustee Sale Number',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.trustee_sales_channel',
      name: 'Trustee Sales Channel',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: trusteeSalesChannel,
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      // NOTE: options are attached below in columnSelector
      apiRef: 'trustee.sales_agent_code',
      name: 'Trustee Sales Agent',
      searchType: SEARCH_TYPES.SUGGEST_ASYNC_ONE_TIME,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'listing_configuration.mlh_seller_code',
      name: 'Seller',
      searchType: SEARCH_TYPES.SUGGEST_ASYNC,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColProps,
          width: 50,
        },
      },
    },
    {
      apiRef: 'seller_property.street_description',
      name: 'Address',
      columnDefinition: {
        partialMatch: true,
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      apiRef: 'seller_property.municipality',
      name: 'City',
      columnDefinition: {
        partialMatch: true,
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      apiRef: 'seller_property.country_primary_subdivision',
      name: 'State',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: statesList,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'seller_property.postal_code',
      name: 'Zip',
      columnDefinition: {
        partialMatch: true,
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'seller_property.country_secondary_subdivision',
      name: 'County',
      searchType: SEARCH_TYPES.SUGGEST_ASYNC,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'created_date',
      name: 'Created Date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      searchType: SEARCH_TYPES.DATE,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'created_by',
      name: 'Created By',
      foreignKey: LOOKUP_TYPES.USER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'update_date',
      name: 'Updated Date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      searchType: SEARCH_TYPES.DATE,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'updated_by',
      name: 'Updated By',
      foreignKey: LOOKUP_TYPES.USER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'auction.reserve_price',
      name: 'Reserve/Credit Bid',
      requiredPermissions: JSON.stringify(['MRW', 'ML', 'MRR']),
      formatOptions: {
        withCents: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.CURRENCY,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'auction.auction_date',
      name: 'Listing Sale Date',
      dataType: DATA_TYPES.DATE_AS_ISO,
      searchType: SEARCH_TYPES.DATE,
      formatOptions: {
        dateFormat: 'datePattern',
        pattern: API_LOCAL_DATE_FORMAT,
        displayFormat: UI_DATE_ALTERNATE_FORMAT,
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'trustee.sale_time',
      name: 'Listing Sale Time',
      dataType: DATA_TYPES.TIME,
      searchType: SEARCH_TYPES.TIME,
      columnDefinition: {
        type: COLUMN_TYPES.TIME,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'trustee.sale_location',
      name: 'Sale Location',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColProps,
          width: COLUMN_STANDARD_SIZES.LARGE,
          isVisible: false,
        },
      },
    },
    {
      name: 'Venue Start Date',
      apiRef: 'venue.venue_schedule.bid_start_local_date',
      dataType: DATA_TYPES.DATE_AS_ISO,
      searchType: SEARCH_TYPES.DATE,
      formatOptions: {
        dateFormat: 'datePattern',
        pattern: API_LOCAL_DATE_FORMAT,
        displayFormat: UI_DATE_ALTERNATE_FORMAT,
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Venue Start Time',
      apiRef: 'venue.venue_schedule.bid_start_local_time',
      dataType: DATA_TYPES.TIME,
      searchType: SEARCH_TYPES.TIME,
      columnDefinition: {
        type: COLUMN_TYPES.TIME,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Venue End Date',
      apiRef: 'venue.venue_schedule.initial_bidding_end_local_date',
      dataType: DATA_TYPES.DATE_AS_ISO,
      searchType: SEARCH_TYPES.DATE,
      formatOptions: {
        dateFormat: 'datePattern',
        pattern: API_LOCAL_DATE_FORMAT,
        displayFormat: UI_DATE_ALTERNATE_FORMAT,
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Venue End Time',
      apiRef: 'venue.venue_schedule.initial_bidding_end_local_time',
      dataType: DATA_TYPES.TIME,
      searchType: SEARCH_TYPES.TIME,
      columnDefinition: {
        type: COLUMN_TYPES.TIME,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'auction.starting_bid',
      name: 'Starting Bid',
      dataType: DATA_TYPES.NUMBER,
      formatOptions: {
        withCents: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.CURRENCY,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'parties.party_id',
      name: 'Asset Manager',
      dataType: DATA_TYPES.NUMBER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_status',
      name: 'Listing Status',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: listingStatusList,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      apiRef: 'venue.venue_name',
      name: 'Venue',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.run_number',
      name: 'Run Number',
      dataType: DATA_TYPES.NUMBER,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.item_number',
      name: 'Item No',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.total_bedrooms',
      name: 'Bedrooms',
      dataType: DATA_TYPES.NUMBER_RANGE,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.total_bathrooms',
      name: 'Bathrooms',
      dataType: DATA_TYPES.NUMBER_RANGE,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.living_size',
      name: 'Square Footage',
      dataType: DATA_TYPES.NUMBER_RANGE,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.structure_type_code',
      name: 'Structure Type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: globalPhysicalStructures,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.lot_size',
      name: 'Lot Size',
      dataType: DATA_TYPES.NUMBER_RANGE,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.apn',
      name: 'APN',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.year_built',
      name: 'Year Built',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.unaltered_valuation',
      name: 'ERV',
      formatOptions: {
        withCents: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.CURRENCY,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.product_type',
      name: 'Product Type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: productTypes,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.asset_type',
      name: 'Asset Type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: assetTypes,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.interior_access_allowed',
      name: 'Interior Access Allowed',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.buyer_premium_available',
      name: "Buyer's Premium",
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.is_reserve_displayed',
      name: 'Reserve Disclosed',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_configuration.financing_available',
      name: 'Financing Available',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'is_searchable',
      name: 'Is Searchable',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'is_published',
      name: 'Is Published',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      name: 'Coming Soon',
      apiRef: 'is_coming_soon',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'listing_configuration.occupancy_status',
      name: 'Occupancy Status',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: [
        { value: 'OCCUPIED', text: 'Occupied' },
        { value: 'VACANT', text: 'Vacant' },
        { value: 'UNKNOWN', text: 'Unknown' },
      ],
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'schedule.scheduling_status',
      name: 'Scheduling Status',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: schedulingStatus,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'schedule.failure_reason',
      name: 'Scheduling Unresolved Reason',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: schedulingFailureReason,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
    },
    {
      apiRef: 'external_sync_status',
      name: 'External Sync Status',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
        },
      },
      options: [
        { value: 'PENDING', text: 'Pending' },
        { value: 'SUCCESS', text: 'Success' },
      ],
    },
    {
      name: 'View',
      apiRef: '__actions__',
      isUiOnly: true,
      columnDefinition: {
        actions: [
          ACTION_CELL_ACTION_TYPES.EDIT_DETAILS,
          ACTION_CELL_ACTION_TYPES.REMOVE_ROW,
        ],
        type: COLUMN_TYPES.ACTION,
        colProps: { width: 100, fixedRight: true, align: 'center' },
      },
    },
  ],
  filterClauses: [],
  sortBy: [
    {
      field: 'auction.auction_date',
      order: 'desc',
    },
  ],
}

const linkColumnAction = (actionIndex, rowIndex, primaryId) => (
  <Link
    style={{ margin: -8, padding: 8 }}
    key={actionIndex}
    to={`/property/listings/${primaryId}/view`}
    data-elm-id={`action_edit_${rowIndex}`}
    target='_blank'
    rel='noopener noreferrer'
  >
    <Icon size='large' style={{ marginTop: -3 }} name='eye' color='info' />
  </Link>
)

const removeRow = (
  actionIndex,
  rowIndex,
  primaryId,
  data,
  onRowAction,
  userAuthorities
) => {
  return (
    <Popup
      content='Remove Registered Listing'
      key={actionIndex}
      size='tiny'
      trigger={
        <Icon
          link
          color='error'
          size='large'
          name='delete'
          style={{ marginTop: -3 }}
          data-elm-id={`user_registered_listing_action_remove_${rowIndex}`}
          onClick={() => {
            onRowAction(primaryId)
          }}
        />
      }
    />
  )
}

const columnAction = actionCellActionType => {
  switch (actionCellActionType) {
    case ACTION_CELL_ACTION_TYPES.EDIT_DETAILS:
      return linkColumnAction
    case ACTION_CELL_ACTION_TYPES.REMOVE_ROW:
      return removeRow
  }
}

/**
 *
 * @param {*} fieldDefinitions
 * THIS SELECTOR IS TO MAINTAIN BACKWARDS COMPATIBILITY WITH EVERYTHING IN TOOLKIT
 * It will change/ be removed when everything there changes. This is so we can refactor slowly
 */
export const columnSelector = (state, userAuthorities, suggestions) =>
  state.fieldDefinitions
    .filter(field =>
      field.requiredPermissions
        ? JSON.parse(field.requiredPermissions).every(authority =>
            userAuthorities.includes(authority)
          )
        : true
    )
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(columnAction)
      if (col.dataType) column.dataType = col.dataType
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions

      // use the suggestions (fixed list) for options resolution in cell
      // TODO: consider a way to embed this into DataTable itself if that makes sense.
      if (column.field === 'trustee.sales_agent_code') {
        column.options = _.get(
          suggestions,
          ['suggestionData', column.field],
          []
        )
      }

      return column
    })

export default reducers.dataTable.dataTable(
  USER_REGISTERED_LISTINGS,
  tableConfig
)
